import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Snackbar,
  TextField,
} from "@mui/material";
import { Lock, Person, Email, CheckCircle } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useResetPasswordMutation, useRequestEmailVerificationMutation, useGetUserQuery } from "features/auth/auth";
import { useAppSelector } from "features/store";
import CustomPasswordField from "./common/CustomPasswordField";
import CustomButton from "./common/CustomButton";

type ChangePasswordInputs = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

type UpdateEmailInputs = {
  email: string;
};

interface FieldErrors {
  old_password?: string;
  new_password?: string[];
}

const passwordRequirements = [
  { label: "At least 8 characters long", test: (p: string) => p.length >= 8 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { label: "One symbol", test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
];

type SettingsSection = "profile" | "email" | "password";

const menuItems: { id: SettingsSection; label: string }[] = [
  { id: "profile", label: "Profile" },
  { id: "email", label: "Email" },
  { id: "password", label: "Password" },
];

export default function AccountSettings() {
  const { user } = useAppSelector((state) => state.auth);
  const [activeSection, setActiveSection] = useState<SettingsSection>("profile");
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [requestEmailVerification, { isLoading: isUpdatingEmail }] = useRequestEmailVerificationMutation();
  const { refetch: refetchUser } = useGetUserQuery();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [emailError, setEmailError] = useState<string>("");
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordInputs>();

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    reset: resetEmail,
    formState: { errors: emailErrors },
  } = useForm<UpdateEmailInputs>();

  const watchPassword = watch("new_password", "");

  const passwordStrength = useMemo(() => {
    const passed = passwordRequirements.filter((req) => req.test(watchPassword)).length;
    return (passed / passwordRequirements.length) * 100;
  }, [watchPassword]);

  const getStrengthLabel = () => {
    if (passwordStrength <= 25) return { label: "Weak", color: "#f44336" };
    if (passwordStrength <= 50) return { label: "Fair", color: "#ff9800" };
    if (passwordStrength <= 75) return { label: "Good", color: "#2196f3" };
    return { label: "Strong", color: "#4caf50" };
  };

  const strengthInfo = getStrengthLabel();

  const showNotification = (message: string, severity: "success" | "error") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const onSubmit: SubmitHandler<ChangePasswordInputs> = async (data) => {
    setFieldErrors({});
    try {
      await resetPassword({
        old_password: data.current_password,
        new_password: data.new_password,
      }).unwrap();
      showNotification("Password successfully updated", "success");
      reset();
    } catch (error: any) {
      if (error?.data?.status === "error" && error?.data?.error) {
        const errorData = error.data.error;
        const newFieldErrors: FieldErrors = {};

        if (errorData.old_password) {
          newFieldErrors.old_password = errorData.old_password;
        }
        if (errorData.new_password) {
          newFieldErrors.new_password = errorData.new_password;
        }

        setFieldErrors(newFieldErrors);
      } else {
        showNotification(
          error?.data?.message || "Failed to update password. Please try again.",
          "error"
        );
      }
      console.error("Password change failed:", error);
    }
  };

  const onSubmitEmail: SubmitHandler<UpdateEmailInputs> = async (data) => {
    setEmailError("");
    try {
      await requestEmailVerification({ email: data.email }).unwrap();
      setSubmittedEmail(data.email);
      setEmailSent(true);
      resetEmail();
    } catch (error: any) {
      if (error?.data?.status === "error" && error?.data?.error?.email) {
        setEmailError(error.data.error.email[0] || error.data.error.email);
      } else {
        showNotification(
          error?.data?.message || "Failed to send verification email. Please try again.",
          "error"
        );
      }
    }
  };

  const handleBackToEmailForm = () => {
    setEmailSent(false);
    setSubmittedEmail("");
  };

  const renderProfileSection = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: "rgb(31, 93, 107)", fontWeight: 600 }}>
        Profile Information
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "rgb(31, 93, 107)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Person sx={{ fontSize: 40, color: "#fff" }} />
        </Box>
        <Box>
          <Typography variant="h6">{user?.username}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.is_calibrate_admin ? "Administrator" : "User"}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Email Address
        </Typography>
        <Typography variant="body1">
          {user?.email || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Not set</span>}
        </Typography>
      </Box>
    </Box>
  );

  const renderEmailSection = () => {
    if (emailSent) {
      return (
        <Box>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CheckCircle sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, color: "rgb(31, 93, 107)", fontWeight: 600 }}>
              Check Your Email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We sent a verification link to <strong>{submittedEmail}</strong>.
              Click the link in the email to verify your address.
            </Typography>
            <CustomButton onClick={handleBackToEmailForm} sx={{ mt: 2 }}>
              SEND TO DIFFERENT EMAIL
            </CustomButton>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1, color: "rgb(31, 93, 107)", fontWeight: 600 }}>
          Email Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {user?.email
            ? "Update your email address for account recovery and notifications."
            : "Add an email address to enable password recovery."}
        </Typography>
        {user?.email && (
          <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Current Email
            </Typography>
            <Typography variant="body1">{user.email}</Typography>
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmitEmail(onSubmitEmail)} noValidate>
          <TextField
            fullWidth
            label={user?.email ? "New Email Address" : "Email Address"}
            id="email"
            type="email"
            autoComplete="email"
            error={Boolean(emailErrors.email) || Boolean(emailError)}
            helperText={emailErrors.email?.message || emailError || ""}
            InputProps={{
              startAdornment: <Email sx={{ color: "gray", mr: 1 }} />,
            }}
            sx={{ mb: 3 }}
            {...registerEmail("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
          />
          {isUpdatingEmail ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          ) : (
            <CustomButton type="submit">
              {user?.email ? "UPDATE EMAIL" : "ADD EMAIL"}
            </CustomButton>
          )}
        </Box>
      </Box>
    );
  };

  const renderPasswordSection = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, color: "rgb(31, 93, 107)", fontWeight: 600 }}>
        Change Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ensure your account stays secure by using a strong password.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <CustomPasswordField
          label="Current Password"
          id="current_password"
          autoComplete="current-password"
          error={Boolean(fieldErrors.old_password)}
          helperText={fieldErrors.old_password || ""}
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("current_password", {
            required: "Current password is required",
          })}
        />

        <CustomPasswordField
          label="New Password"
          id="new_password"
          autoComplete="new-password"
          error={Boolean(errors.new_password) || Boolean(fieldErrors.new_password)}
          helperText={
            fieldErrors.new_password?.join("\n") ||
            errors.new_password?.message ||
            ""
          }
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("new_password", {
            required: "New password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
            validate: {
              hasUppercase: (v) => /[A-Z]/.test(v) || "Must contain uppercase letter",
              hasNumber: (v) => /[0-9]/.test(v) || "Must contain a number",
              hasSymbol: (v) =>
                /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(v) || "Must contain a symbol",
            },
          })}
        />

        {watchPassword && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: strengthInfo.color,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: strengthInfo.color, fontWeight: "bold", mt: 0.5 }}
            >
              {strengthInfo.label}
            </Typography>

            <Box sx={{ mt: 1 }}>
              {passwordRequirements.map((req, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  display="flex"
                  alignItems="center"
                  sx={{
                    color: req.test(watchPassword) ? "#4caf50" : "text.secondary",
                  }}
                >
                  {req.test(watchPassword) ? "\u2713" : "\u25CB"} {req.label}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <CustomPasswordField
          label="Confirm New Password"
          id="confirm_password"
          autoComplete="new-password"
          error={Boolean(errors.confirm_password)}
          helperText={errors.confirm_password?.message || ""}
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("confirm_password", {
            required: "Please confirm your new password",
            validate: (value) =>
              value === watchPassword || "Passwords do not match",
          })}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress />
          </Box>
        ) : (
          <CustomButton type="submit" sx={{ mt: 3 }}>
            UPDATE PASSWORD
          </CustomButton>
        )}
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return renderProfileSection();
      case "email":
        return renderEmailSection();
      case "password":
        return renderPasswordSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, color: "rgb(31, 93, 107)", fontWeight: "bold" }}
      >
        Account Settings
      </Typography>

      <Box sx={{ display: "flex", gap: 4 }}>
        <Box sx={{ width: 180, flexShrink: 0, pt: 1 }}>
          {menuItems.map((item) => (
            <Typography
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              sx={{
                py: 0.75,
                fontSize: 15,
                color: activeSection === item.id ? "rgb(31, 93, 107)" : "#555",
                fontWeight: activeSection === item.id ? 600 : 400,
                cursor: "pointer",
                "&:hover": {
                  color: "rgb(31, 93, 107)",
                },
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 4,
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid #e0e0e0",
          }}
        >
          {renderContent()}
        </Box>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
